import { useMemo } from "react";
import {
	ReportItem,
	ReportStreamAssets,
	useReportCascadeFilters,
} from "./useReportStreamFilter";
import { useReportStreamParams } from "./useReportStreamParams";
import {
	countByStatus,
	filterByStatuses,
	getCascadeFilters,
	getPageSlice,
	getStreamFilterOptions,
	matchesStreamSearch,
	ReportStreamParamOptions,
	sortStreamRows,
	StreamSortValues,
} from "../utils/reporting/reportStream";
import { ReportStatusKey } from "../utils/reporting/reportStatusOptions";

// A filter of the stream's own report type, ahead of the operator, project,
// well and operation filters: the audit type, say. Values match exactly.
export type ReportStreamTypeFilter<T> = {
	// The filter's key in the address.
	key: string;
	label: string;
	getValue: (row: T) => unknown;
	getOptionLabel: (value: string) => string;
};

// What differs between the streams' lists.
export type ReportStreamConfig<T> = {
	// The status figures, in their order, and those on by default.
	statuses: readonly ReportStatusKey[];
	defaultStatuses: readonly ReportStatusKey[];
	// Hides other tenants' internal reports from a tenant other than the
	// master tenant.
	hasInternalExternal: boolean;
	filters: readonly ReportStreamTypeFilter<T>[];
	// What each sortable column sorts by, by its sort key.
	sortValues: StreamSortValues<T>;
};

// A filter in the stream's toolbar.
export type ReportStreamSelect = {
	key: string;
	label: string;
	options: { label: string; value: string }[];
	value: string[];
	onChange: (values: string[]) => void;
};

const CASCADE_KEYS = ["operator", "project", "well", "operation"];

// A report stream's list: the reports the tenant can see, through the
// cascade filters, the type's own filters, the search, the status figures,
// the sort and the page, all kept in the page's address
// (useReportStreamParams). The figures count what the search and filters
// leave, whatever figures are on. The search gets the projects, wells and
// operations the filters load, which name a report's place. `config` and
// `getSearchTexts` must keep their identity between renders.
export function useReportStream<T extends ReportItem>(
	data: T[] | undefined,
	config: ReportStreamConfig<T>,
	getSearchTexts: (row: T, assets: ReportStreamAssets) => unknown[],
	currentTenantKey: string | undefined,
	isMasterTenant: boolean | undefined
) {
	const paramOptions = useMemo<ReportStreamParamOptions>(
		() => ({
			statuses: config.statuses,
			defaultStatuses: config.defaultStatuses,
			filterKeys: [
				...config.filters.map((filter) => filter.key),
				...CASCADE_KEYS,
			],
			sortKeys: Object.keys(config.sortValues),
		}),
		[config]
	);
	const params = useReportStreamParams(paramOptions);
	const { state } = params;

	const selection = {
		operators: state.filters.operator,
		projects: state.filters.project,
		wells: state.filters.well,
		operations: state.filters.operation,
	};
	const cascade = useReportCascadeFilters(
		data,
		currentTenantKey,
		isMasterTenant,
		config.hasInternalExternal,
		selection,
		(next, changed) =>
			params.setFilters(
				getCascadeFilters(
					next,
					changed === "operators",
					cascade.operatorOptions.length,
					selection.operators
				)
			)
	);

	const typeFilterOptions = useMemo(
		() =>
			config.filters.map((filter) =>
				getStreamFilterOptions(
					cascade.visibleData,
					filter.getValue,
					filter.getOptionLabel
				)
			),
		[config, cascade.visibleData]
	);

	const matched = useMemo(
		() =>
			cascade.cascadedData.filter(
				(row) =>
					config.filters.every((filter) => {
						const chosen = state.filters[filter.key];
						const value = filter.getValue(row);
						return (
							chosen.length === 0 ||
							(typeof value === "string" && chosen.includes(value))
						);
					}) &&
					matchesStreamSearch(getSearchTexts(row, cascade.assets), state.search)
			),
		[
			cascade.cascadedData,
			cascade.assets,
			config,
			state.filters,
			state.search,
			getSearchTexts,
		]
	);

	const counts = useMemo(() => countByStatus(matched), [matched]);
	// The reports a figure can show; one at any other status is never listed.
	const total = useMemo(
		() => filterByStatuses(cascade.visibleData, config.statuses).length,
		[cascade.visibleData, config]
	);
	const sorted = useMemo(
		() =>
			sortStreamRows(
				filterByStatuses(matched, state.statuses),
				state.sort,
				config.sortValues
			),
		[matched, state.statuses, state.sort, config]
	);
	const page = getPageSlice(sorted, state.page, state.pageSize);

	// With one operator there's nothing to choose, unless one is chosen.
	const hasSeveralOperators = cascade.operatorOptions.length > 1;
	const showOperator = hasSeveralOperators || selection.operators.length > 0;
	const selects: ReportStreamSelect[] = [
		...config.filters.map((filter, index) => ({
			key: filter.key,
			label: filter.label,
			options: typeFilterOptions[index],
			value: state.filters[filter.key],
			onChange: (values: string[]) =>
				params.setFilters({ [filter.key]: values }),
		})),
		...(showOperator
			? [
					{
						key: "operator",
						label: "Operator",
						options: cascade.operatorOptions,
						value: selection.operators,
						onChange: cascade.handleOperatorChange,
					},
				]
			: []),
		{
			key: "project",
			label: "Project",
			options: cascade.projectOptions,
			value: selection.projects,
			onChange: cascade.handleProjectChange,
		},
		{
			key: "well",
			label: "Well",
			options: cascade.wellOptions,
			value: selection.wells,
			onChange: cascade.handleWellChange,
		},
		{
			key: "operation",
			label: "Operation",
			options: cascade.operationOptions,
			value: selection.operations,
			onChange: cascade.handleOperationChange,
		},
	];

	return {
		figures: config.statuses,
		statuses: state.statuses,
		setStatuses: params.setStatuses,
		counts,
		searchInput: params.searchInput,
		setSearchInput: params.setSearchInput,
		selects,
		filtersActive: params.filtersActive,
		reset: params.reset,
		sort: state.sort,
		setSort: params.setSort,
		// Every report the tenant can see at the stream's statuses, and those
		// the filters leave.
		total,
		shownCount: sorted.length,
		page: { ...page, pageSize: state.pageSize },
		setPage: params.setPage,
		setPageSize: params.setPageSize,
		// For the rows: the operator shows under a number only when the reports
		// have more than one, as the Operator filter shows.
		hasSeveralOperators,
		assets: cascade.assets,
	};
}

export type ReportStreamView<T extends ReportItem> = ReturnType<
	typeof useReportStream<T>
>;
